import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { message, Typography } from 'antd';
import type { Personnel } from '@/types/personnel';
import type { PersonnelDetail } from '@/types/personnelDetail';
import { usersService } from '@/services/users.service';
import { departmentsService } from '@/services/departments.service';
import type { UserUpdateRequest } from '@shared/services/users.service';
import { handleApiError } from '@/utils/errorHandler';
import { formatDateDisplay } from '@/utils/dateHelpers';
import { loadAvatarFromData } from '@/utils/avatarLoader';
import { mapUserDetailToPersonnelDetail } from '@/features/personnel/personnelDataMapper';
import { exportPersonnelToExcel } from '@/features/personnel/exportPersonnelExcel';
import { useAuth } from '@/contexts/AuthContext';

export interface UsePersonnelDetailResult {
  // data
  detailData: PersonnelDetail | null;
  loading: boolean;
  error: string | null;
  avatarUri: string | null;
  pendingUpdateRequest: UserUpdateRequest | null;
  currentValuesForReview: Record<string, unknown>;
  departmentNamesForReview: Record<number, string>;
  idCardsForReview: Array<{ id: number; documentType: string; fileUrl?: string; status?: string }>;
  currentUserId: number | undefined;

  // modals
  isHistoryModalOpen: boolean;
  setIsHistoryModalOpen: (open: boolean) => void;
  isRejectModalOpen: boolean;
  setIsRejectModalOpen: (open: boolean) => void;
  isApproveModalOpen: boolean;
  setIsApproveModalOpen: (open: boolean) => void;

  // actions
  handleRefresh: () => void;
  handleExport: () => Promise<void>;
  handleRejectConfirm: (reason: string) => Promise<void>;
  handleApproveConfirm: (confirmClearingCollections?: boolean) => Promise<void>;
  formatFieldValue: (fieldName: string, value: any) => string;
  renderValueWithComparison: (
    fieldKey: string,
    currentValue: any,
    updateDataField?: string,
    customClassName?: string
  ) => React.ReactNode;
}

export const usePersonnelDetail = (
  personnel?: Personnel | null
): UsePersonnelDetailResult => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ employeeCode?: string }>();
  const { user } = useAuth();
  const [detailData, setDetailData] = useState<PersonnelDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avatarUri, setAvatarUri] = useState<string | null>(null);
  const [pendingUpdateRequest, setPendingUpdateRequest] = useState<UserUpdateRequest | null>(null);
  const [currentValuesForReview, setCurrentValuesForReview] = useState<Record<string, unknown>>({});
  const [departmentNamesForReview, setDepartmentNamesForReview] = useState<Record<number, string>>({});
  const [idCardsForReview, setIdCardsForReview] = useState<Array<{ id: number; documentType: string; fileUrl?: string; status?: string }>>([]);
  const [currentUserId, setCurrentUserId] = useState<number | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);

  // Fetch user detail từ API với error handling đầy đủ
  const fetchPersonnelDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Xác định user ID cần fetch
      // Ưu tiên: employeeCode từ params > personnel từ state > user hiện tại
      let userId: number | undefined;

      if (params?.employeeCode) {
        // Tìm user theo employeeCode
        try {
          const foundUser = await usersService.getUserByEmployeeCode(
            params.employeeCode
          );
          userId = foundUser.id;
        } catch (err: any) {
          console.error('Error searching user by employeeCode:', err);
          const errorMsg = 'Không thể tìm kiếm nhân sự';
          setError(errorMsg);
          message.error(errorMsg);
          navigate(-1);
          return;
        }
      } else if (personnel?.id) {
        userId = parseInt(personnel.id, 10);
      } else if (user?.id) {
        // Fallback về profile của chính mình
        userId = parseInt(user.id.toString(), 10);
      }

      if (!userId || isNaN(userId)) {
        const errorMsg = 'Không tìm thấy thông tin nhân sự';
        setError(errorMsg);
        message.error(errorMsg);
        navigate(-1);
        return;
      }

      // Lưu userId vào state để dùng cho EditHistoryModal
      setCurrentUserId(userId);

      // Fetch user detail, departments và các dữ liệu liên quan song song để tăng tốc độ
      const [
        userDetail,
        departmentsResponse,
        educationHistory,
        workExperience,
        familyMembers,
        certificates,
        partyHistory,
        idCards,
        personalFiles,
        horoscope,
      ] = await Promise.all([
        usersService.getUserById(userId),
        departmentsService.getDepartments().catch(() => ({ data: [] })),
        usersService.getEducationHistory(userId).catch((err) => {
          console.warn('Failed to fetch education history:', err);
          return [];
        }),
        usersService.getWorkExperience(userId).catch((err) => {
          console.warn('Failed to fetch work experience:', err);
          return [];
        }),
        usersService.getFamilyMembers(userId).catch((err) => {
          console.warn('Failed to fetch family members:', err);
          return [];
        }),
        usersService.getCertificates(userId).catch((err) => {
          console.warn('Failed to fetch certificates:', err);
          return [];
        }),
        usersService.getPartyHistory(userId).catch((err) => {
          console.warn('Failed to fetch party history:', err);
          return [];
        }),
        usersService.getIdCards(userId).catch((err) => {
          console.warn('Failed to fetch ID cards:', err);
          return [];
        }),
        usersService.getPersonalFiles(userId).catch((err) => {
          console.warn('Failed to fetch personal files:', err);
          return [];
        }),
        usersService.getHoroscope(userId).catch((err) => {
          console.warn('Failed to fetch horoscope:', err);
          return null;
        }),
      ]);

      // Log để debug
      console.log('Fetched data for user', userId, ':', {
        educationHistory: educationHistory.length,
        workExperience: workExperience.length,
        familyMembers: familyMembers.length,
        certificates: certificates.length,
        partyHistory: partyHistory.length,
        idCards: idCards.length,
        personalFiles: personalFiles.length,
        horoscope: horoscope ? 'exists' : 'null',
      });

      // Lấy tên phòng ban
      setCurrentValuesForReview(userDetail as unknown as Record<string, unknown>);
      setDepartmentNamesForReview(Object.fromEntries(departmentsResponse.data.map((department) => [Number(department.id), department.name])));
      let departmentName = '';
      if (userDetail.departmentId) {
        const department = departmentsResponse.data.find((d: any) => d.id === userDetail.departmentId);
        departmentName = department?.name || '';
      }

      const isApprovedCard = (card: { status?: string; isApproved?: boolean }) =>
        (card.status || '').toLowerCase() === 'approved' || card.isApproved === true;

      const approvedIdCards = idCards.filter(isApprovedCard);
      setIdCardsForReview(idCards);

      const withVersion = (url?: string) => (url ? encodeURI(url) : undefined);

      // Tìm ID card images (chỉ hiển thị bản đã duyệt)
      const idCardFront = withVersion(approvedIdCards.find((card) => card.documentType === 'id_card_front')?.fileUrl);
      const idCardBack = withVersion(approvedIdCards.find((card) => card.documentType === 'id_card_back')?.fileUrl);
      const passportCover = withVersion(approvedIdCards.find((card) => card.documentType === 'passport_cover')?.fileUrl);
      const passportPhoto = withVersion(
        approvedIdCards.find(
          (card) => card.documentType === 'passport_additional' || card.documentType === 'passport_photo',
        )?.fileUrl,
      );

      // Map sang PersonnelDetail format
      const mappedData = mapUserDetailToPersonnelDetail(userDetail, {
        educationHistory: educationHistory.map((edu) => ({
          trainingInstitution: edu.trainingInstitution,
          major: edu.major,
          trainingForm: edu.trainingForm,
          educationLevel: edu.educationLevel,
          fromDate: edu.fromDate,
          toDate: edu.toDate,
        })),
        workExperience: workExperience.map((exp) => ({
          company: exp.company,
          position: exp.position,
          department: exp.department,
          salary: exp.salary,
          fromDate: exp.fromDate,
          toDate: exp.toDate,
        })),
        familyMembers: familyMembers.map((member) => ({
          relationship: member.relationship,
          fullName: member.fullName,
          birthYear: member.birthYear,
          phone: member.phone,
          occupation: member.occupation,
          workplace: member.workplace,
          address: member.address,
        })),
        certificates: certificates.map((cert) => ({
          id: cert.id,
          certificateNumber: cert.certificateNumber,
          type: cert.certificateType,
          issueDate: cert.fromDate,
          expiryDate: cert.toDate,
          notes: cert.notes,
          issuingUnit: cert.issuingUnit,
          attachments: cert.attachments || [],
        })),
        partyHistory: partyHistory.map((party) => ({
          cardNumber: party.cardNumber,
          form: party.form,
          fromDate: party.fromDate,
          toDate: party.toDate,
          admissionPlace: party.admissionPlace,
          transferPlace: party.transferPlace,
        })),
        personalFiles: personalFiles.map((file) => ({
          fileName: file.fileName,
          fileUrl: file.fileUrl,
        })),
        idCardFront,
        idCardBack,
        passportCover,
        passportPhoto,
        horoscope: horoscope?.imageUrl
          ? encodeURI(horoscope.imageUrl)
          : undefined,
      });

      // Cập nhật department từ department name
      if (departmentName && mappedData.currentJob) {
        mappedData.currentJob.department = departmentName;
      }

      setDetailData(mappedData);

      // Kiểm tra xem có yêu cầu cập nhật đang chờ duyệt không
      try {
        const updateRequestsResponse = await usersService.getUpdateRequests({
          userId,
          status: 'pending',
          limit: 1,
        });

        if (updateRequestsResponse.data && updateRequestsResponse.data.length > 0) {
          setPendingUpdateRequest(updateRequestsResponse.data[0]);
        } else {
          setPendingUpdateRequest(null);
        }
      } catch (err) {
        // Nếu không có quyền hoặc lỗi, không hiển thị nút
        console.error('Error fetching pending update requests:', err);
        setPendingUpdateRequest(null);
      }

      // Load avatar từ detailData.avatar
      try {
        const avatar = await loadAvatarFromData(mappedData.avatar);
        setAvatarUri(avatar);
      } catch (err) {
        console.error('Error loading avatar:', err);
        setAvatarUri(null);
      }
    } catch (err: any) {
      console.error('Error fetching personnel detail:', err);
      const errorMsg = err?.message || 'Không thể tải thông tin nhân sự';
      setError(errorMsg);
      handleApiError(err, errorMsg);
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [params?.employeeCode, personnel?.id, user?.id, navigate]);

  useEffect(() => {
    fetchPersonnelDetail();
  }, [fetchPersonnelDetail, refreshKey]);

  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Xử lý refresh khi có flag từ notification
  useEffect(() => {
    const locationState = location.state as { refresh?: boolean; timestamp?: number } | null;
    if (locationState?.refresh) {
      // Trigger refresh bằng cách tăng refreshKey
      setRefreshKey(prev => prev + 1);
    }
  }, [location]);

  // Listen for avatar-updated event để refresh avatar khi có user upload avatar mới
  useEffect(() => {
    const handleAvatarUpdated = (event: CustomEvent<{ userId: string }>) => {
      // Nếu avatar được cập nhật cho user đang xem, refresh lại data
      if (detailData && event.detail.userId === detailData.id.toString()) {
        handleRefresh();
      }
    };

    window.addEventListener('avatar-updated', handleAvatarUpdated as EventListener);
    return () => {
      window.removeEventListener('avatar-updated', handleAvatarUpdated as EventListener);
    };
  }, [detailData, handleRefresh]);

  const handleExport = useCallback(async () => {
    if (!detailData) {
      message.warning('Không có dữ liệu để xuất file');
      return;
    }

    try {
      // Tạo tên file: TenNhanSu_MaNhanSu (hỗ trợ tiếng Việt)
      const fullName = (detailData.fullName || 'NhanSu').replace(/\s+/g, '_').replace(/[<>:"/\\|?*]/g, '');
      const employeeCode = (detailData.employeeCode || 'MaNS').replace(/\s+/g, '_').replace(/[<>:"/\\|?*]/g, '');
      const fileName = `${fullName}_${employeeCode}.xlsx`;
      await exportPersonnelToExcel(detailData, fileName);
      message.success('Xuất file Excel thành công');
    } catch (err: any) {
      console.error('Error exporting file:', err);
      handleApiError(err, 'Không thể xuất file: ' + (err.message || 'Lỗi không xác định'));
    }
  }, [detailData]);

  const handleRejectConfirm = useCallback(async (reason: string) => {
    if (!pendingUpdateRequest) return;

    try {
      const result = await usersService.rejectUpdateRequest(pendingUpdateRequest.id, reason);
      if (!result.success || result.data?.status !== 'rejected') {
        throw new Error(result.message || 'Server chưa xác nhận yêu cầu đã bị từ chối');
      }
      message.success('Đã từ chối yêu cầu cập nhật thông tin');
      setPendingUpdateRequest(null);
      // Refresh lại dữ liệu
      handleRefresh();
    } catch (err: any) {
      console.error('Error rejecting update request:', err);
      handleApiError(err, 'Không thể từ chối yêu cầu cập nhật');
      throw err;
    }
  }, [pendingUpdateRequest, handleRefresh]);

  const handleApproveConfirm = useCallback(async (confirmClearingCollections = false) => {
    if (!pendingUpdateRequest) return;

    try {
      const result = await usersService.approveUpdateRequest(pendingUpdateRequest.id, { confirmClearingCollections });
      if (!result.success || result.data?.status !== 'approved') {
        throw new Error(result.message || 'Server chưa xác nhận yêu cầu đã được duyệt');
      }
      message.success(`Đã duyệt yêu cầu #${pendingUpdateRequest.id}`);
      setPendingUpdateRequest(null);
      // Refresh lại dữ liệu
      handleRefresh();
    } catch (err: any) {
      console.error('Error approving update request:', err);
      handleApiError(err, 'Không thể duyệt yêu cầu cập nhật');
      throw err;
    }
  }, [pendingUpdateRequest, handleRefresh]);

  // Helper function để format giá trị từ tiếng Anh sang tiếng Việt
  const formatFieldValue = useCallback((fieldName: string, value: any): string => {
    if (!value || value === '') return String(value || '');

    const strValue = String(value).toLowerCase().trim();

    // Format gender
    if (fieldName === 'gender') {
      const genderMap: Record<string, string> = {
        'male': 'Nam',
        'female': 'Nữ',
        'other': 'Khác',
      };
      return genderMap[strValue] || String(value);
    }

    // Format marital status
    if (fieldName === 'maritalStatus') {
      const maritalStatusMap: Record<string, string> = {
        'single': 'Độc thân',
        'married': 'Đã kết hôn',
        'divorced': 'Ly hôn',
        'widowed': 'Góa',
      };
      return maritalStatusMap[strValue] || String(value);
    }

    // Format military service
    if (fieldName === 'militaryService') {
      const militaryServiceMap: Record<string, string> = {
        'yes': 'Đã hoàn thành',
        'no': 'Hoãn nghĩa vụ',
        'exempt': 'Miễn nghĩa vụ',
      };
      return militaryServiceMap[strValue] || String(value);
    }

    // Format labor type
    if (fieldName === 'laborType') {
      const laborTypeMap: Record<string, string> = {
        'full-time': 'Toàn thời gian',
        'fulltime': 'Toàn thời gian',
        'part-time': 'Bán thời gian',
        'parttime': 'Bán thời gian',
        'contract': 'Hợp đồng',
        'intern': 'Thực tập',
        'internship': 'Thực tập',
      };
      return laborTypeMap[strValue] || String(value);
    }

    // Format general education level
    if (fieldName === 'educationLevel' || fieldName === 'generalEducationLevel' || fieldName === 'highestEducationLevel') {
      const generalEducationLevelMap: Record<string, string> = {
        'primary': 'Tiểu học',
        'secondary': 'Trung học cơ sở',
        'highschool': 'Trung học phổ thông',
      };
      return generalEducationLevelMap[strValue] || String(value);
    }

    // Format academic education level
    if (fieldName === 'academicEducationLevel') {
      const academicEducationLevelMap: Record<string, string> = {
        'secondary': 'Trung cấp',
        'college': 'Cao đẳng',
        'university': 'Đại học',
        'graduate': 'Sau đại học',
      };
      return academicEducationLevelMap[strValue] || String(value);
    }

    // Format ethnicity (giữ nguyên logic từ page)
    if (fieldName === 'ethnicity') {
      const ethnicityMap: Record<string, string> = {
        'kinh': 'Kinh',
        'tay': 'Tày',
        'thai': 'Thái',
        'muong': 'Mường',
        'khmer': 'Khmer',
        'hoa': 'Hoa',
        'nung': 'Nùng',
        'hmong': 'H\'Mông',
        'dao': 'Dao',
        'giarai': 'Gia Rai',
        'ede': 'Ê Đê',
        'bana': 'Ba Na',
        'xodang': 'Xơ Đăng',
        'sanchay': 'Sán Chay',
        'coho': 'Cơ Ho',
        'cham': 'Chăm',
        'sandiu': 'Sán Dìu',
        'hre': 'Hrê',
        'mnong': 'M\'Nông',
        'raglai': 'Ra Glai',
        'stieng': 'Stiêng',
        'bruvankieu': 'Bru - Vân Kiều',
        'tho': 'Thổ',
        'giay': 'Giáy',
        'co': 'Cơ Tu',
        'taoi': 'Tà Ôi',
        'ma': 'Mạ',
        'khmu': 'Khơ Mú',
        'boy': 'Bố Y',
        'xinhmun': 'Xinh Mun',
        'cong': 'Cống',
        'hanhi': 'Hà Nhì',
        'lahu': 'La Hủ',
        'phula': 'Phù Lá',
        'lachi': 'La Chí',
        'pathen': 'Pa Then',
        'churu': 'Chu Ru',
        'lao': 'Lào',
        'laha': 'La Ha',
        'khang': 'Kháng',
        'lu': 'Lự',
        'lolo': 'Lô Lô',
        'mang': 'Mảng',
        'phathay': 'Pà Thẻn',
        'colao': 'Cờ Lao',
        'brau': 'Brâu',
        'odu': 'Ơ Đu',
        'romam': 'Rơ Măm',
        'sila': 'Si La',
        'pupeo': 'Pu Péo',
        'other': 'Khác',
      };
      return ethnicityMap[strValue] || String(value);
    }

    return String(value);
  }, []);

  const formatComparableValue = useCallback((fieldName: string, value: any): string => {
    if (value === undefined || value === null || value === '') return '';

    let normalizedValue = value;

    if (typeof normalizedValue === 'string' && (fieldName === 'hometown')) {
      normalizedValue = normalizedValue.replace('Huyện ', '').replace('Tỉnh ', '').replace(', Việt Nam', '');
    }

    if (
      fieldName === 'dateOfBirth' ||
      fieldName === 'startDate' ||
      fieldName === 'officialContractDate' ||
      fieldName === 'idIssueDate' ||
      fieldName === 'idExpiryDate' ||
      fieldName === 'passportIssueDate' ||
      fieldName === 'passportExpiryDate'
    ) {
      const formattedDate = formatDateDisplay(String(normalizedValue));
      if (formattedDate) {
        normalizedValue = formattedDate;
      }
    }

    return formatFieldValue(fieldName, normalizedValue);
  }, [formatFieldValue]);

  // Helper function để render giá trị với so sánh nếu có thay đổi
  const renderValueWithComparison = useCallback((
    fieldKey: string,
    currentValue: any,
    updateDataField?: string,
    customClassName?: string
  ): React.ReactNode => {
    const { Text } = Typography;

    const formattedCurrentValue = formatComparableValue(fieldKey, currentValue);

    if (!pendingUpdateRequest) {
      return React.createElement(Text, { className: customClassName }, formattedCurrentValue || 'Chưa cập nhật');
    }

    const updateData = pendingUpdateRequest.updateData || {};
    const updateField = updateDataField || fieldKey;
    const newValue = updateData[updateField];

    if (newValue === undefined || newValue === null || newValue === '') {
      return React.createElement(Text, { className: customClassName }, formattedCurrentValue || 'Chưa cập nhật');
    }

    const formattedNewValue = formatComparableValue(updateField, newValue);

    const normalizedOld = String(formattedCurrentValue || '').trim().toLowerCase();
    const normalizedNew = String(formattedNewValue || '').trim().toLowerCase();

    const hasChange = normalizedNew !== '' && normalizedNew !== normalizedOld;

    if (!hasChange) {
      return React.createElement(Text, { className: customClassName }, formattedCurrentValue || 'Chưa cập nhật');
    }

    return React.createElement(
      'div',
      { style: { display: 'flex', flexDirection: 'column', gap: '4px' } },
      React.createElement(Text, { style: { fontSize: '16px', fontWeight: 600, color: '#1890ff' } }, formattedNewValue),
      React.createElement(Text, { style: { fontSize: '14px', color: '#8c8c8c', textDecoration: 'line-through' } }, formattedCurrentValue || 'Chưa cập nhật')
    );
  }, [pendingUpdateRequest, formatComparableValue]);

  return {
    // data
    detailData,
    loading,
    error,
    avatarUri,
    pendingUpdateRequest,
    currentValuesForReview,
    departmentNamesForReview,
    idCardsForReview,
    currentUserId,

    // modals
    isHistoryModalOpen,
    setIsHistoryModalOpen,
    isRejectModalOpen,
    setIsRejectModalOpen,
    isApproveModalOpen,
    setIsApproveModalOpen,

    // actions
    handleRefresh,
    handleExport,
    handleRejectConfirm,
    handleApproveConfirm,
    formatFieldValue,
    renderValueWithComparison,
  };
};
